import { useState, useCallback, useMemo } from 'react';

export interface DateRange {
    startTime: Date;
    endTime: Date;
}

export type StatusFilter = 'all' | 'active' | 'inactive';

export type SortField = 'name' | 'createdAt';
export type SortDirection = 'asc' | 'desc';

export interface SortOption {
    field: SortField;
    direction: SortDirection;
}

export const SORT_OPTIONS: {
    id: string;
    field: SortField;
    direction: SortDirection;
    labelKey: string;
}[] = [
    {
        id: 'name-asc',
        field: 'name',
        direction: 'asc',
        labelKey: 'sort.promptAZ',
    },
    {
        id: 'name-desc',
        field: 'name',
        direction: 'desc',
        labelKey: 'sort.promptZA',
    },
    {
        id: 'createdAt-desc',
        field: 'createdAt',
        direction: 'desc',
        labelKey: 'sort.newestFirst',
    },
    {
        id: 'createdAt-asc',
        field: 'createdAt',
        direction: 'asc',
        labelKey: 'sort.oldestFirst',
    },
];

interface AlertRulesFiltersState {
    dateRange: DateRange | null;
    statusFilter: StatusFilter;
    sortOption: SortOption | null;
    hasActiveFilters: boolean;
    setDateRange: (range: DateRange | null) => void;
    setStatusFilter: (status: StatusFilter) => void;
    setSortOption: (option: SortOption | null) => void;
    clearAllFilters: () => void;
    getQueryParams: () => {
        startTime?: number;
        endTime?: number;
        active?: boolean;
        orderBy?: string;
    };
}

export function useAlertRulesFilters(): AlertRulesFiltersState {
    const [dateRange, setDateRange] = useState<DateRange | null>(null);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [sortOption, setSortOption] = useState<SortOption | null>({
        field: 'createdAt',
        direction: 'desc',
    });

    const hasActiveFilters = useMemo(() => {
        return dateRange !== null || statusFilter !== 'all';
    }, [dateRange, statusFilter]);

    const clearAllFilters = useCallback(() => {
        setDateRange(null);
        setStatusFilter('all');
    }, []);

    const getQueryParams = useCallback(() => {
        return {
            ...(dateRange && {
                startTime: dateRange.startTime.getTime(),
                endTime: dateRange.endTime.getTime(),
            }),
            ...(statusFilter !== 'all' && {
                active: statusFilter === 'active',
            }),
            ...(sortOption && {
                orderBy: `${sortOption.field} ${sortOption.direction}`,
            }),
        };
    }, [dateRange, statusFilter, sortOption]);

    return {
        dateRange,
        statusFilter,
        sortOption,
        hasActiveFilters,
        setDateRange,
        setStatusFilter,
        setSortOption,
        clearAllFilters,
        getQueryParams,
    };
}
